// MUI Theme
import { Chip, CircularProgress, Tooltip } from '@mui/material';
import { Download } from '@mui/icons-material';

// Utils
import { dateHeader, formatCellDate } from './timeModeCell';

// Types
import { AppPermissions } from '../../constants/Permissions';
import { ColumnDef } from '@tanstack/react-table';
import { ApkInfo } from '../../types/ApkInfo';

// Components
import { AuthGuard } from '../../components/common/AuthGuard';
import GridAction from '../../components/common/GridAction';

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
}

export const makeAppReleasesColumn = (
  onDownloadClick: (apkInfo: ApkInfo) => void,
  onEdit: (apkInfo: ApkInfo) => void,
  onDelete: (fileName: string) => void,
  downloadingFile: string | null,
) => {
  const col: ColumnDef<ApkInfo>[] = [
    {
      id: 'id',
      header: 'Actions',
      accessorKey: 'id',
      size: 75,
      cell: ({ row }) => {
        return (
          <GridAction
            onEdit={onEdit}
            editPermission={AppPermissions.Apk.Edit}
            onDeleted={onDelete}
            deletePermission={AppPermissions.Apk.Delete}
            row={row.original}
            id={row.original.fileName}
          />
        );
      },
    },
    {
      header: 'File Name',
      size: 275,
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {row.original.displayName}
          {row.original.isTestBuild && <Chip label='Test Build' color='info' size='small' />}
          {row.original.isCurrentMarketplaceBuild && <Chip label='Current Marketplace Build' color='warning' size='small' />}
        </div>
      ),
    },
    {
      header: 'Version',
      size: 80,
      accessorKey: 'order',
      cell: ({ row }) => row.original.version,
    },
    {
      header: dateHeader('Upload Date'),
      accessorKey: 'lastModifiedDate',
      size: 120,
      cell: ({ row, table }) => formatCellDate(table, row.original.lastModifiedDate),
    },
    {
      header: 'App Size',
      accessorKey: 'sizeInBytes',
      size: 80,
      cell: ({ row }) => (row.original.sizeInBytes ? formatSize(row.original.sizeInBytes) : '-'),
    },
    {
      header: 'Download APK',
      accessorKey: 'action',
      enableSorting: false,
      size: 50,
      cell: ({ row }) => {
        const isDownloading = downloadingFile === row.original.fileName;

        return (
          <AuthGuard permission={AppPermissions.Apk.Read}>
            <Tooltip title={isDownloading ? 'Downloading…' : 'Download App'} placement='right'>
              <span style={{ display: 'inline-flex', cursor: isDownloading ? 'default' : 'pointer' }}>
                {isDownloading ? (
                  <CircularProgress size='0.7em' style={{ height: '0.7em', width: '0.7em' }} color='inherit' />
                ) : (
                  <Download
                    style={{ height: '0.7em' }}
                    color='action'
                    cursor='pointer'
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownloadClick(row.original);
                    }}
                  />
                )}
              </span>
            </Tooltip>
          </AuthGuard>
        );
      },
    },
  ];

  return col;
};
